import { FunctionComponent, useEffect, useState } from 'react';
import { BoxIconLine, GroupIcon, FileIcon } from '../../icons';
import { TDashboardStats } from '../../types/dashboard';
import { useToggle as useLoading, useToggle } from '../../hooks/useToggle';
import { getDashboardStats } from '../../services/dashboard';
import { numberToLocale } from '../../utils/helper';
import { LoaderSpinner } from '../Loader';
import { Modal } from '../ui/modal';
import { StatCard } from '../common/StatCard';

export default function Metrics() {
  const [stats, setStats] = useState<TDashboardStats | null>(null);
  const { state: isLoading, off } = useLoading(true);

  const fetchStats = async (signal: AbortSignal) => {
    const res = await getDashboardStats({ signal }).finally(() => {
      off();
    });
    if (res?.data) setStats(res.data);
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchStats(abortController.signal);
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center mt-12">
        <LoaderSpinner />
      </div>
    );
  }

  if (!stats && !isLoading) {
    return <div>No stats to show</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 md:gap-6">
      {/* <!-- Metric Item Start --> */}
      <StatCard
        icon={<GroupIcon className="text-gray-800 size-6 dark:text-white/90" />}
        title="Number of Users"
        stat={numberToLocale(stats!.noOfStudents)}
      />

      {/* <!-- Metric Item Start --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl dark:bg-gray-800">
          <GroupIcon className="text-gray-800 size-6 dark:text-white/90" />
        </div>
        <div className="flex items-end justify-between mt-5">
          <div className="w-full">
            <span className="text-sm text-gray-500 dark:text-gray-400 flex justify-between w-full">
              <p>Onboarded Students</p>{' '}
              <OnBoardedStudentsModeInfo
                stats={{
                  noOfStudentsOnPersonalDetails:
                    stats?.noOfStudentsOnPersonalDetails,
                  noOfStudentsOnCustomizeAcademicPath:
                    stats?.noOfStudentsOnCustomizeAcademicPath,
                  noOfStudentsOnMilestonesAndTracker:
                    stats?.noOfStudentsOnMilestonesAndTracker,
                  noOfStudentsOnAICourses: stats?.noOfStudentsOnAICourses,
                  noOfStudentsCompleted: stats?.noOfStudentsCompleted,
                }}
                total={stats!.noOfStudents}
              />
            </span>
            <h4 className="mt-2 font-bold text-gray-800 text-title-sm dark:text-white/90">
              {numberToLocale(stats!.noOfStudents)}
            </h4>
          </div>
        </div>
      </div>
      {/* <!-- Metric Item End --> */}
      <StatCard
        icon={<BoxIconLine className="text-gray-800 dark:text-white/90" />}
        title="Active Courses"
        stat={numberToLocale(stats!.noOfCourses)}
      />
      <StatCard
        icon={<FileIcon className="text-gray-800 dark:text-white/90" />}
        title="Courses with Content"
        stat={numberToLocale(stats!.noOfCoursesWithContent)}
      />

      <StatCard
        icon={<FileIcon className="text-gray-800 dark:text-white/90" />}
        title="Courses without Content"
        stat={numberToLocale(stats!.noOfCoursesWithoutContent)}
      />

      <StatCard
        icon={<BoxIconLine className="text-gray-800 dark:text-white/90" />}
        title="Courses with Quizzes"
        stat={numberToLocale(stats!.noOfCoursesWithoutQuiz)}
      />
    </div>
  );
}

const OnBoardedStudentsModeInfo: FunctionComponent<{
  stats: Partial<
    Pick<
      TDashboardStats,
      | 'noOfStudentsOnPersonalDetails'
      | 'noOfStudentsOnCustomizeAcademicPath'
      | 'noOfStudentsOnMilestonesAndTracker'
      | 'noOfStudentsOnAICourses'
      | 'noOfStudentsCompleted'
    >
  >;
  total?: number;
}> = ({ stats, total }) => {
  const { state, toggle, off } = useToggle();
  const {
    noOfStudentsOnPersonalDetails,
    noOfStudentsOnCustomizeAcademicPath,
    noOfStudentsOnMilestonesAndTracker,
    noOfStudentsOnAICourses,
    noOfStudentsCompleted,
  } = stats;

  return (
    <div>
      <button
        className="flex flex-row items-center gap-1 text-[#092256]"
        onClick={toggle}
      >
        More Info
      </button>
      <Modal
        isOpen={state}
        onClose={off}
        isFullscreen={false}
        className="max-w-[700px]"
      >
        <div className="px-6 pb-6">
          <h1 className="md:text-2xl lg:text-3xl font-bold w-full text-center text-black">
            Current Status of {total} Onboarded Users
          </h1>
          <ul className="mt-6 list-decimal px-6">
            <li className="py-1">
              <strong>Profile Info - </strong> {noOfStudentsOnPersonalDetails}
            </li>
            <li className="py-1">
              <strong>Academic Path - </strong>
              {noOfStudentsOnCustomizeAcademicPath}
            </li>
            <li className="py-1">
              <strong>MileStones Tracker - </strong>
              {noOfStudentsOnMilestonesAndTracker}
            </li>
            <li className="py-1">
              <strong>AI Courses - </strong> {noOfStudentsOnAICourses}
            </li>
            <li className="py-1">
              <strong>Completed Onboarding - </strong> {noOfStudentsCompleted}
            </li>
          </ul>
        </div>
      </Modal>
    </div>
  );
};
